import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Loader2,
  Save,
  ShoppingBag,
  Unplug,
} from 'lucide-react';
import { authApi, tiktokShopApi } from '@/services/api';

/**
 * TikTok Shop configure panel — Partner Center credentials + how-to guide.
 */
export default function TikTokShopIntegrationTab() {
  const qc = useQueryClient();
  const [showSecret, setShowSecret] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [showRefresh, setShowRefresh] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [guideOpen, setGuideOpen] = useState(true);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => (await authApi.getCompanyProfile()).data.message,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const features: string[] = Array.isArray(profile?.features) ? profile.features : [];
  const packageAllows =
    Boolean(profile?.allow_tiktok) ||
    features.includes('allow_tiktok') ||
    (Array.isArray(profile?.selected_add_ons) &&
      profile.selected_add_ons.some(
        (a: string | { status?: string; feature_key?: string; addon_code?: string }) => {
          if (typeof a === 'string') return a === 'allow_tiktok';
          if (a?.status && a.status !== 'Active') return false;
          return a?.feature_key === 'allow_tiktok' || a?.addon_code === 'allow_tiktok';
        },
      ));

  const { data: status, refetch } = useQuery({
    queryKey: ['tiktokShopStatus'],
    queryFn: async () => (await tiktokShopApi.getStatus()).data.message,
    enabled: packageAllows,
  });

  const [form, setForm] = useState({
    enable_tiktok: false,
    tiktok_shop_id: '',
    tiktok_shop_cipher: '',
    tiktok_service_id: '',
    tiktok_market: 'UK',
    tiktok_app_key: '',
    tiktok_app_secret: '',
    tiktok_access_token: '',
    tiktok_refresh_token: '',
    tiktok_sync_products_mode: 'Selected',
    tiktok_auto_confirm_orders: false,
    tiktok_auto_fetch_orders: false,
  });

  useEffect(() => {
    if (!profile) return;
    setForm((f) => ({
      ...f,
      enable_tiktok: Boolean(profile.enable_tiktok),
      tiktok_shop_id: profile.tiktok_shop_id || '',
      tiktok_shop_cipher: profile.tiktok_shop_cipher || '',
      tiktok_service_id: profile.tiktok_service_id || '',
      tiktok_market: profile.tiktok_market || 'UK',
      tiktok_app_key: profile.tiktok_app_key || '',
      tiktok_sync_products_mode: profile.tiktok_sync_products_mode || 'Selected',
      tiktok_auto_confirm_orders: Boolean(profile.tiktok_auto_confirm_orders),
      tiktok_auto_fetch_orders: Boolean(profile.tiktok_auto_fetch_orders),
    }));
  }, [profile]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { ...form };
      if (!form.tiktok_app_secret) delete payload.tiktok_app_secret;
      if (!form.tiktok_access_token) delete payload.tiktok_access_token;
      if (!form.tiktok_refresh_token) delete payload.tiktok_refresh_token;
      await authApi.updateCompanyProfile(payload);
    },
    onSuccess: () => {
      toast.success('TikTok Shop settings saved');
      qc.invalidateQueries({ queryKey: ['profile'] });
      void refetch();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || e.message || 'Save failed'),
  });

  const run = async (fn: () => Promise<any>, okText: string) => {
    setMsg(null);
    try {
      const res = await fn();
      const text = typeof res === 'string' ? res : res?.message || okText;
      setMsg({ ok: true, text: typeof text === 'string' ? text : okText });
      void refetch();
      qc.invalidateQueries({ queryKey: ['profile'] });
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.message || e.message || 'Failed' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!packageAllows) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 space-y-3 max-w-xl">
        <p className="font-medium">TikTok Shop add-on required</p>
        <p>Contact Support to purchase the TikTok Shop integration for your company.</p>
        <Link
          to="/support?subject=Enable%20TikTok%20Shop%20integration"
          className="btn-primary text-sm w-fit inline-flex"
        >
          Contact Support
        </Link>
      </div>
    );
  }

  const connected = Boolean(status?.connected);

  return (
    <div className="space-y-5 max-w-3xl min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
            <ShoppingBag className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">TikTok Shop</h2>
            <p className="text-sm text-gray-500">UK / ROW Partner Center — listings, stock and orders</p>
          </div>
        </div>
        <span
          className={`text-xs font-medium px-2.5 py-1 rounded-full ${
            connected ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {connected ? 'Connected' : status?.connection_status || 'Disconnected'}
        </span>
      </div>

      {msg && (
        <div
          className={`rounded-lg border px-3 py-2 text-sm flex gap-2 ${
            msg.ok ? 'border-emerald-100 bg-emerald-50 text-emerald-900' : 'border-red-100 bg-red-50 text-red-800'
          }`}
        >
          {msg.ok ? (
            <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          )}
          <span>{msg.text}</span>
        </div>
      )}

      <div className="rounded-xl border border-slate-300 bg-gradient-to-br from-slate-50 to-white overflow-hidden min-w-0">
        <button
          type="button"
          onClick={() => setGuideOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left min-w-0"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-900 min-w-0">
            <BookOpen className="w-4 h-4 shrink-0" />
            <span className="truncate">How to get credentials from TikTok Shop</span>
          </span>
          {guideOpen ? (
            <ChevronUp className="w-4 h-4 text-slate-700 shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-700 shrink-0" />
          )}
        </button>

        {guideOpen && (
          <div className="px-4 pb-4 space-y-4 text-sm text-gray-700 min-w-0 overflow-x-hidden">
            <p className="text-gray-600 break-words">
              Collect App Key, App Secret, Access Token and Refresh Token from TikTok Shop Partner Center after
              the seller authorises your app — then paste them into the form below.
            </p>

            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-900 min-w-0">
              <p className="font-semibold mb-1">Path to the form</p>
              <p className="font-mono text-[12px] tracking-tight leading-relaxed break-words whitespace-normal">
                <a
                  href="https://partner.tiktokshop.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="underline decoration-slate-500/50 font-bold"
                >
                  partner.tiktokshop.com
                </a>
                {' '}→ App &amp; Service →{' '}
                <span className="font-bold underline decoration-slate-500/40">Create app &amp; service</span>
                {' '}→ Credentials
              </p>
              <p className="mt-1.5 text-gray-600 break-words">
                UK / ROW Partner Center (use{' '}
                <code className="text-[11px]">partner.us.tiktokshop.com</code> only for US). Docs:{' '}
                <a
                  href="https://partner.tiktokshop.com/docv2/page/create-your-app"
                  target="_blank"
                  rel="noreferrer"
                  className="text-slate-800 underline break-all"
                >
                  Create your app
                </a>
              </p>
            </div>

            <p className="text-sm font-semibold text-gray-900">On TikTok Shop Partner Center</p>
            <ol className="space-y-3 list-decimal list-inside">
              <li>
                <span className="font-medium text-gray-900">Developer onboarding</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Register as a developer. UK partners usually need compliance / legal review before live API
                  access — budget time for that.
                </p>
              </li>

              <li>
                <span className="font-medium text-gray-900">Create an app</span>
                <p className="mt-1 ml-5 text-gray-600">
                  <strong>App &amp; Service → Create app &amp; service</strong>. Choose Custom (single shop) or
                  Public. Select Market <strong>UK</strong>, Seller type <strong>Local</strong>, and turn on{' '}
                  <strong>Enable API</strong>.
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs space-y-1">
                  <p>
                    <span className="text-gray-500">Default name:</span>{' '}
                    <code className="font-semibold text-gray-900">SaleDesk POS</code>
                  </p>
                  <p>
                    <span className="text-gray-500">Market:</span> <span className="font-medium">UK</span>
                  </p>
                  <p>
                    <span className="text-gray-500">Enable API:</span>{' '}
                    <span className="font-medium">on</span>
                  </p>
                  <p>
                    <span className="text-gray-500">Redirect URL:</span> your SaleDesk HTTPS callback (or
                    Support-provided URL)
                  </p>
                </div>
              </li>

              <li>
                <span className="font-medium text-gray-900">Copy App Key &amp; App Secret</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Open the app → <strong>Credentials</strong>. Copy App Key and reveal App Secret. Service Id is
                  shown beside the app name.
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-950 flex gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>A newly rotated App Secret can take up to ~10 minutes to become active.</span>
                </div>
              </li>

              <li>
                <span className="font-medium text-gray-900">Select API scopes</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Enable product, inventory and order scopes the app needs. Sellers see these scopes when they
                  authorise.
                </p>
              </li>

              <li>
                <span className="font-medium text-gray-900">Seller authorise → tokens</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Open the seller authorize URL (
                  <code className="text-[11px]">services.tiktokshop.com/open/authorize</code>
                  ). After approval, exchange the one-time <code>code</code> for tokens at{' '}
                  <code className="text-[11px]">auth.tiktok-shops.com/api/v2/token/get</code>.
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-emerald-200 bg-emerald-50/80 p-3 text-xs text-emerald-950 space-y-2">
                  <p className="font-semibold">Paste into SaleDesk (below)</p>
                  <ol className="list-decimal list-inside space-y-1.5 text-gray-800">
                    <li>App Key → <strong>App Key</strong></li>
                    <li>App Secret → <strong>App Secret</strong></li>
                    <li>access_token → <strong>Access Token</strong></li>
                    <li>refresh_token → <strong>Refresh Token</strong></li>
                    <li>Shop ID / shop_cipher when returned</li>
                    <li>Save, then <strong>Test connection</strong></li>
                  </ol>
                  <p className="text-gray-600 border-t border-emerald-200/80 pt-2">
                    Never share App Secret or tokens in chat.
                  </p>
                </div>
              </li>

              <li>
                <span className="font-medium text-gray-900">Shop ID &amp; Shop Cipher</span>
                <p className="mt-1 ml-5 text-gray-600">
                  From the authorized shops response (or Seller Center). Many OpenAPI calls require{' '}
                  <strong>shop_cipher</strong>.
                </p>
              </li>
            </ol>

            <div className="rounded-lg bg-white/80 border border-slate-200 p-3 text-xs text-gray-600 space-y-1">
              <p className="font-medium text-gray-800">Tips</p>
              <p>• Keep SaleDesk SKUs identical to TikTok Shop seller SKUs.</p>
              <p>• Test connection uses refresh-token exchange to verify App Key / Secret.</p>
              <p>
                • Docs:{' '}
                <a
                  href="https://partner.tiktokshop.com/docv2/page/tts-developer-guide"
                  target="_blank"
                  rel="noreferrer"
                  className="text-slate-800 underline"
                >
                  TTS developer guide
                </a>
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 space-y-4">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={form.enable_tiktok}
            onChange={(e) => setForm({ ...form, enable_tiktok: e.target.checked })}
          />
          Enable TikTok Shop integration
        </label>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-600">Market</label>
            <input
              className="input-field mt-1"
              value={form.tiktok_market}
              onChange={(e) => setForm({ ...form, tiktok_market: e.target.value })}
              placeholder="UK"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Service ID</label>
            <input
              className="input-field mt-1"
              value={form.tiktok_service_id}
              onChange={(e) => setForm({ ...form, tiktok_service_id: e.target.value })}
              placeholder="Beside app name in Partner Center"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Shop ID</label>
            <input
              className="input-field mt-1"
              value={form.tiktok_shop_id}
              onChange={(e) => setForm({ ...form, tiktok_shop_id: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Shop Cipher</label>
            <input
              className="input-field mt-1"
              value={form.tiktok_shop_cipher}
              onChange={(e) => setForm({ ...form, tiktok_shop_cipher: e.target.value })}
              placeholder="From authorized shops"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Product sync mode</label>
            <select
              className="input-field mt-1"
              value={form.tiktok_sync_products_mode}
              onChange={(e) => setForm({ ...form, tiktok_sync_products_mode: e.target.value })}
            >
              <option value="Selected">Selected (Show on TikTok)</option>
              <option value="All active">All active</option>
              <option value="Manual">Manual only</option>
            </select>
          </div>
          <div className="flex flex-col gap-2 justify-end pb-1">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.tiktok_auto_fetch_orders}
                onChange={(e) => setForm({ ...form, tiktok_auto_fetch_orders: e.target.checked })}
              />
              Auto-fetch orders
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.tiktok_auto_confirm_orders}
                onChange={(e) => setForm({ ...form, tiktok_auto_confirm_orders: e.target.checked })}
              />
              Auto-confirm mapped orders
            </label>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4 space-y-3">
          <p className="text-sm font-medium text-gray-800">App credentials &amp; tokens</p>
          <p className="text-xs text-gray-500">
            From Partner Center → App credentials and seller authorization token exchange. Never share in chat.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600">App Key</label>
              <input
                className="input-field mt-1"
                value={form.tiktok_app_key}
                onChange={(e) => setForm({ ...form, tiktok_app_key: e.target.value })}
                autoComplete="off"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">
                App Secret {profile?.has_tiktok_app_secret ? '(saved)' : ''}
              </label>
              <div className="relative mt-1">
                <input
                  className="input-field pr-10"
                  type={showSecret ? 'text' : 'password'}
                  value={form.tiktok_app_secret}
                  placeholder={profile?.has_tiktok_app_secret ? '••••••••' : ''}
                  onChange={(e) => setForm({ ...form, tiktok_app_secret: e.target.value })}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                  onClick={() => setShowSecret((v) => !v)}
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">
                Access Token {profile?.has_tiktok_access_token ? '(saved)' : ''}
              </label>
              <div className="relative mt-1">
                <input
                  className="input-field pr-10"
                  type={showAccess ? 'text' : 'password'}
                  value={form.tiktok_access_token}
                  placeholder={profile?.has_tiktok_access_token ? '••••••••' : ''}
                  onChange={(e) => setForm({ ...form, tiktok_access_token: e.target.value })}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                  onClick={() => setShowAccess((v) => !v)}
                >
                  {showAccess ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">
                Refresh Token {profile?.has_tiktok_refresh_token ? '(saved)' : ''}
              </label>
              <div className="relative mt-1">
                <input
                  className="input-field pr-10"
                  type={showRefresh ? 'text' : 'password'}
                  value={form.tiktok_refresh_token}
                  placeholder={profile?.has_tiktok_refresh_token ? '••••••••' : ''}
                  onChange={(e) => setForm({ ...form, tiktok_refresh_token: e.target.value })}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                  onClick={() => setShowRefresh((v) => !v)}
                >
                  {showRefresh ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="button"
            className="btn-primary text-sm inline-flex items-center gap-1.5"
            disabled={saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={() =>
              void run(async () => {
                const r = await tiktokShopApi.testConnection();
                return r.data.message;
              }, 'Connection OK')
            }
          >
            Test connection
          </button>
          <button
            type="button"
            className="btn-secondary text-sm inline-flex items-center gap-1.5 text-red-700"
            onClick={() =>
              void run(async () => {
                await tiktokShopApi.disconnect();
                setForm((f) => ({
                  ...f,
                  tiktok_app_secret: '',
                  tiktok_access_token: '',
                  tiktok_refresh_token: '',
                }));
                return { message: 'Disconnected' };
              }, 'Disconnected')
            }
          >
            <Unplug className="w-3.5 h-3.5" />
            Disconnect
          </button>
          <Link to="/channel-orders?channel=tiktok" className="btn-secondary text-sm w-fit">
            Marketplace Orders
          </Link>
        </div>

        <p className="text-xs text-gray-500">
          Catalogue / order sync will use these Partner Center credentials. Prefer a Refresh Token so Test
          connection can verify App Key / Secret.
        </p>
      </div>
    </div>
  );
}
